/**
 * Scroll affordance for horizontal rails.
 *
 * The rail itself is native overflow with scroll-snap — swipe, trackpad,
 * scrollbar drag and Tab-into-a-card all work with no script at all. This adds
 * only the pointer chrome: two buttons that scroll by exactly one card, hidden
 * whenever they would do nothing.
 *
 * Markup contract:
 *   [data-rail]                  the scroll container
 *   [data-rail-scroll="prev"]    button, anywhere inside the same parent
 *   [data-rail-scroll="next"]
 *
 * The step is measured from a live card rather than hardcoded, because card
 * width and gap are both viewport-dependent clamps — a fixed number would drift
 * out of snap alignment as the viewport changes.
 */

const RAIL = '[data-rail]';

/** Sub-pixel slack, so a rail scrolled fully right still counts as "at the end". */
const EDGE_EPSILON = 1;

/**
 * Distance from one card's leading edge to the next, in px.
 *
 * Measured from a live card rather than hardcoded: card width and gap are both
 * viewport-dependent clamps, so a fixed number drifts out of snap alignment as
 * the viewport changes. Exported because the media gallery's auto-advance
 * scrolls by the same step as these buttons and should not re-derive it.
 *
 * @param {Element} track
 * @returns {number}
 */
export function railStep(track) {
  const card = track.firstElementChild;
  if (!card) return track.clientWidth;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  return card.getBoundingClientRect().width + gap;
}

class Rail {
  #onScroll = null;
  #onResize = null;

  constructor(track) {
    this.track = track;
    // Buttons are siblings inside the viewport wrapper, not descendants of the
    // scroll container — they must not scroll away with the content.
    this.buttons = [...(track.parentElement?.querySelectorAll('[data-rail-scroll]') ?? [])];
    if (!this.buttons.length) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const direction = button.dataset.railScroll === 'prev' ? -1 : 1;
        this.track.scrollBy({ left: railStep(this.track) * direction, behavior: 'smooth' });
      });
    });

    this.#onScroll = () => this.sync();
    this.#onResize = () => this.sync();
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.sync();
  }

  sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflowing = scrollWidth - clientWidth > EDGE_EPSILON;
    // Right-to-left locales report scrollLeft negative or reversed; the
    // magnitude is what "distance from the start" means in both directions.
    const from = Math.abs(scrollLeft);

    this.buttons.forEach((button) => {
      const isPrev = button.dataset.railScroll === 'prev';
      const atEdge = isPrev
        ? from <= EDGE_EPSILON
        : from >= scrollWidth - clientWidth - EDGE_EPSILON;
      button.hidden = !overflowing || atEdge;
    });
  }

  destroy() {
    if (this.#onScroll) this.track.removeEventListener('scroll', this.#onScroll);
    if (this.#onResize) window.removeEventListener('resize', this.#onResize);
  }
}

const instances = new WeakMap();

const init = (scope = document) => {
  scope.querySelectorAll?.(RAIL).forEach((track) => {
    if (track.dataset.railReady) return;
    track.dataset.railReady = 'true';
    instances.set(track, new Rail(track));
  });
};

init();

document.addEventListener('shopify:section:load', (event) => init(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.(RAIL).forEach((track) => {
    instances.get(track)?.destroy();
    instances.delete(track);
    delete track.dataset.railReady;
  });
});
